import { DateHelper, Duration, NumberFormat } from "@bryntum/core-thin";
import { displayDecimals, isExtendedDisplay, roundDisplay } from "./displayUnits";

/**
 * Widen the decimals of every duration Bryntum prints as text.
 *
 * `Duration.toString()` formats with `NumberFormat.get("9.#")` — one decimal,
 * hard-coded — and it is what a Duration/Work cell editor shows: the field's
 * `inputValue` getter runs the value through it, and `DurationColumn`'s
 * `defaultEditor` never forwards the column's `decimalPrecision`, so no config
 * reaches it. A cell rendering "11.86 months" therefore opened an editor on
 * "11.9", and confirming committed a value the user had never been shown.
 *
 * This used to live in `patches/@bryntum+core-thin+5.6.9.patch`, but
 * `patch-package` only runs on `postinstall`: a `npm run build` over a
 * node_modules installed before the patch landed never replays it, and both CI
 * workflows install with `--ignore-scripts`. Re-assigning the method at startup
 * ships the format with the bundle instead, and makes it testable.
 *
 * Covers every Bryntum surface that stringifies a duration — Duration and Work
 * cell editors (`EffortField extends DurationField`), the task editor, task
 * tooltips and dependency lag fields.
 */

// Bryntum's number mask: integer part, then one `#` per optional decimal.
// `#` (not `0`) keeps trailing zeros trimmed, so whole values stay "3 months".
// Built per call: the precision follows the portfolio's units, which arrive
// after this module loads.
const magnitudeMask = () => `9.${"#".repeat(displayDecimals())}`;

export const buildDurationToString = (numberFormat, dateHelper) =>
	function toString(useAbbreviation) {
		if (!this.isValid) return "";
		// The extended contract's separator is the point in every language
		// (the unit grammar refuses a comma); NumberFormat follows the Bryntum
		// locale and printed "4,545" in French.
		const magnitude = isExtendedDisplay()
			? roundDisplay(this._magnitude)
			: numberFormat.get(magnitudeMask()).format(this._magnitude);
		const unitName = useAbbreviation
			? dateHelper.getShortNameOfUnit(this._unit, this._magnitude !== 1)
			: dateHelper.getLocalizedNameOfUnit(this._unit, this._magnitude !== 1);
		return `${magnitude} ${unitName}`;
	};

export const installBryntumDurationFormat = ({
	duration = Duration,
	numberFormat = NumberFormat,
	dateHelper = DateHelper,
} = {}) => {
	const proto = duration?.prototype;
	if (!proto || proto._displayDecimalsWired) return;
	proto.toString = buildDurationToString(numberFormat, dateHelper);
	proto._displayDecimalsWired = true;
};
